// Normalises the many shapes an OCR'd or hand-typed nationality can take
// (Spanish country name, English/Spanish demonym, ISO alpha-3 code, …) down to
// a single ISO 3166-1 alpha-2 code. The `/country` endpoint only exposes the
// English name + alpha-2, so anything else ("REPUBLICA DOMINICANA",
// "CANADIAN", "MEX") would otherwise show up as its own distinct bucket.
//
// This map is intentionally curated for the nationalities that realistically
// appear at events (the Americas + common travel countries) rather than an
// exhaustive ISO dataset. Add entries here when a new alias slips through.

// Lowercase + strip accents + collapse whitespace so "República Dominicana",
// "REPUBLICA DOMINICANA" and "republica  dominicana" all key the same.
export const normalizeNationality = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

// One entry per country: every alias (English/Spanish name, demonyms, alpha-3)
// that should collapse to the given ISO alpha-2 code.
const ALIAS_GROUPS: Array<{ iso: string; aliases: string[] }> = [
  { iso: 'DO', aliases: ['republica dominicana', 'dominican republic', 'dominicana', 'dominicano', 'dominican', 'dom'] },
  { iso: 'US', aliases: ['estados unidos', 'estados unidos de america', 'united states', 'united states of america', 'estadounidense', 'american', 'americano', 'americana', 'usa', 'eeuu', 'us'] },
  { iso: 'CA', aliases: ['canada', 'canadian', 'canadiense', 'can'] },
  { iso: 'MX', aliases: ['mexico', 'mexican', 'mexicano', 'mexicana', 'mex'] },
  { iso: 'CO', aliases: ['colombia', 'colombian', 'colombiano', 'colombiana', 'col'] },
  { iso: 'VE', aliases: ['venezuela', 'venezuelan', 'venezolano', 'venezolana', 'ven'] },
  { iso: 'AR', aliases: ['argentina', 'argentine', 'argentinian', 'argentino', 'arg'] },
  { iso: 'BR', aliases: ['brasil', 'brazil', 'brazilian', 'brasileno', 'brasilena', 'brasileiro', 'bra'] },
  { iso: 'CL', aliases: ['chile', 'chilean', 'chileno', 'chilena', 'chl'] },
  { iso: 'PE', aliases: ['peru', 'peruvian', 'peruano', 'peruana', 'per'] },
  { iso: 'EC', aliases: ['ecuador', 'ecuadorian', 'ecuatoriano', 'ecuatoriana', 'ecu'] },
  { iso: 'BO', aliases: ['bolivia', 'bolivian', 'boliviano', 'boliviana', 'bol'] },
  { iso: 'PY', aliases: ['paraguay', 'paraguayan', 'paraguayo', 'paraguaya', 'pry'] },
  { iso: 'UY', aliases: ['uruguay', 'uruguayan', 'uruguayo', 'uruguaya', 'ury'] },
  { iso: 'PA', aliases: ['panama', 'panamanian', 'panameno', 'panamena', 'pan'] },
  { iso: 'CR', aliases: ['costa rica', 'costa rican', 'costarricense', 'cri'] },
  { iso: 'GT', aliases: ['guatemala', 'guatemalan', 'guatemalteco', 'guatemalteca', 'gtm'] },
  { iso: 'HN', aliases: ['honduras', 'honduran', 'hondureno', 'hondurena', 'hnd'] },
  { iso: 'NI', aliases: ['nicaragua', 'nicaraguan', 'nicaraguense', 'nic'] },
  { iso: 'SV', aliases: ['el salvador', 'salvadoran', 'salvadoreno', 'salvadorena', 'slv'] },
  { iso: 'CU', aliases: ['cuba', 'cuban', 'cubano', 'cubana', 'cub'] },
  { iso: 'PR', aliases: ['puerto rico', 'puerto rican', 'puertorriqueno', 'puertorriquena', 'pri'] },
  { iso: 'HT', aliases: ['haiti', 'haitian', 'haitiano', 'haitiana', 'hti'] },
  { iso: 'JM', aliases: ['jamaica', 'jamaican', 'jamaicano', 'jamaicana', 'jam'] },
  { iso: 'ES', aliases: ['espana', 'spain', 'spanish', 'espanol', 'espanola', 'esp'] },
  { iso: 'IT', aliases: ['italia', 'italy', 'italian', 'italiano', 'italiana', 'ita'] },
  { iso: 'FR', aliases: ['francia', 'france', 'french', 'frances', 'francesa', 'fra'] },
  { iso: 'DE', aliases: ['alemania', 'germany', 'german', 'aleman', 'alemana', 'deu', 'ger'] },
  { iso: 'GB', aliases: ['reino unido', 'united kingdom', 'british', 'britanico', 'britanica', 'england', 'inglaterra', 'gbr', 'uk'] },
  { iso: 'PT', aliases: ['portugal', 'portuguese', 'portugues', 'portuguesa', 'prt'] },
  { iso: 'NL', aliases: ['paises bajos', 'holanda', 'netherlands', 'dutch', 'holandes', 'holandesa', 'nld'] },
  { iso: 'CH', aliases: ['suiza', 'switzerland', 'swiss', 'suizo', 'che'] },
  { iso: 'BE', aliases: ['belgica', 'belgium', 'belgian', 'belga', 'bel'] },
  { iso: 'RU', aliases: ['rusia', 'russia', 'russian', 'ruso', 'rusa', 'rus'] },
  { iso: 'CN', aliases: ['china', 'chinese', 'chino', 'chn'] },
  { iso: 'JP', aliases: ['japon', 'japan', 'japanese', 'japones', 'japonesa', 'jpn'] },
  { iso: 'IN', aliases: ['india', 'indian', 'indio', 'ind'] },
];

// Normalized alias → ISO alpha-2.
export const NATIONALITY_ALIAS_TO_ISO: Record<string, string> = ALIAS_GROUPS.reduce(
  (acc, { iso, aliases }) => {
    for (const a of aliases) acc[normalizeNationality(a)] = iso;
    return acc;
  },
  {} as Record<string, string>,
);
